/**
 * FuturesWebSocketContext - Глобальный провайдер для WebSocket фьючерсных контрактов
 * ЗАЧЕМ: Единый источник live цен для всех фьючерсных контрактов в приложении
 * Затрагивает: FuturesSelector (список контрактов + выбранный контракт)
 */

import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useWebSocketControl } from './WebSocketContext';
import { API_BASE_URL, WS_BASE_URL } from '../config/api';

const FuturesWebSocketContext = createContext();

export const useFuturesQuotes = () => {
  const context = useContext(FuturesWebSocketContext);
  if (!context) {
    throw new Error('useFuturesQuotes must be used within FuturesWebSocketProvider');
  }
  return context;
};

const API_KEY = 'XoMSGltU1i5Co0eoO0LMYbDY8YM4_2WLYXrCUNxsdQU';

export const FuturesWebSocketProvider = ({ children }) => {
  const { isWebSocketEnabled } = useWebSocketControl();
  const [quotes, setQuotes] = useState({}); // { symbol: { price, bid, ask, ... } }
  const [allContracts, setAllContracts] = useState(null); // { ES: [...], NQ: [...], GC: [...], CL: [...] }
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isPaused, setIsPaused] = useState(false); // Флаг паузы (подписки отключены, но WS открыт)
  const [dataMode, setDataMode] = useState('live'); // live | historical
  const wsRef = useRef(null);
  const subscribedSymbolsRef = useRef(new Set());
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 10; // Достаточно для production (перезапуски backend)
  const connectRef = useRef(null);
  const subscribeRef = useRef(null);
  const loadedContractsRef = useRef(false);
  const isWebSocketEnabledRef = useRef(true); // Ref для актуального значения, по умолчанию true
  const lastUpdateRef = useRef({}); // Throttle: { symbol: timestamp }
  const THROTTLE_INTERVAL = 3000; // 3 секунды между обновлениями
  const pausedSymbolsRef = useRef(new Set()); // Сохранённые символы при паузе
  
  // Обновляем ref при изменении isWebSocketEnabled
  useEffect(() => {
    isWebSocketEnabledRef.current = isWebSocketEnabled;
    if (!isWebSocketEnabled) {
      console.log('⏸️ [Futures WS] PAUSE - котировки будут игнорироваться');
    } else {
      console.log('▶️ [Futures WS] PLAY - котировки обрабатываются');
    }
  }, [isWebSocketEnabled]);

  // Подключение к WebSocket
  const connect = useCallback(() => {
    if (!isWebSocketEnabled) return;
    if (dataMode !== 'live') return;
    if (subscribedSymbolsRef.current.size === 0) return;
    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) return;

    // Закрываем старое соединение если оно есть
    if (wsRef.current) {
      if (wsRef.current.pingInterval) clearInterval(wsRef.current.pingInterval);
      wsRef.current.close();
      wsRef.current = null;
    }

    try {
      const wsUrl = `${WS_BASE_URL}/api/databento/ws/futures/quotes`;
      setIsConnecting(true);
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        setIsConnecting(false);
        reconnectAttemptsRef.current = 0;
        wsRef.current = ws;

        const API_KEY = 'XoMSGltU1i5Co0eoO0LMYbDY8YM4_2WLYXrCUNxsdQU';
        ws.send(JSON.stringify({ action: 'auth', token: API_KEY }));

        if (subscribedSymbolsRef.current.size > 0) {
          const symbols = Array.from(subscribedSymbolsRef.current);
          console.log(`📤 [Futures WS] Subscribed to ${symbols.length} contracts`);
          ws.send(JSON.stringify({ action: 'subscribe', symbols: symbols }));
        }
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          if (message.type === 'quote') {
            // PAUSE: игнорируем котировки когда на паузе
            if (!isWebSocketEnabledRef.current) {
              return;
            }
            
            // Игнорируем котировки без цены (неликвидные контракты)
            if (message.price === undefined || message.price === null) {
              return;
            }
            
            // Throttle: обновляем только раз в 3 секунды
            const now = Date.now();
            const lastUpdate = lastUpdateRef.current[message.symbol] || 0;
            
            if (now - lastUpdate < THROTTLE_INTERVAL) {
              // Пропускаем обновление - слишком рано
              return;
            }
            
            lastUpdateRef.current[message.symbol] = now;
            
            setQuotes(prev => {
              const existing = prev[message.symbol] || {};
              return {
                ...prev,
                [message.symbol]: {
                  symbol: message.symbol,
                  price: message.price,
                  bid: message.bid !== undefined ? message.bid : existing.bid,
                  ask: message.ask !== undefined ? message.ask : existing.ask,
                  volume: message.volume !== undefined ? message.volume : existing.volume,
                  open_interest: message.open_interest !== undefined ? message.open_interest : existing.open_interest,
                  timestamp: new Date().toISOString(),
                  isLive: true  // Флаг что цена пришла через WebSocket (live)
                }
              };
            });
          } else if (message.type === 'pong') {
            // Keep-alive
          }
        } catch (err) {
          console.error('❌ [Futures WS] Parse error:', err);
        }
      };

      ws.onerror = (error) => {
        console.error('❌ [Futures WS] Error:', error);
        setIsConnected(false);
        setIsConnecting(false);
      };

      ws.onclose = (event) => {
        setIsConnected(false);
        setIsConnecting(false);
        wsRef.current = null;

        const shouldReconnect = isWebSocketEnabledRef.current && dataMode === 'live' && reconnectAttemptsRef.current < maxReconnectAttempts;

        if (shouldReconnect) {
          // Код 1012 = Service Restart — reconnect сразу без задержки
          // ЗАЧЕМ: При перезапуске backend пользователь не должен ждать
          const isServiceRestart = event.code === 1012;
          const delay = isServiceRestart ? 1000 : Math.min(5000 * Math.pow(2, reconnectAttemptsRef.current), 60000);
          
          console.log(`🔄 [Futures WS] Reconnecting in ${delay}ms (code: ${event.code}, attempt: ${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts})`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current += 1;
            connect();
          }, delay);
        } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          console.error('❌ [Futures WS] Max reconnect attempts reached');
        }
      };

      // Ping каждые 30 секунд
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ action: 'ping' }));
        }
      }, 30000);

      ws.pingInterval = pingInterval;

    } catch (err) {
      console.error('❌ [Futures WS] Create error:', err);
    }
  }, [isWebSocketEnabled, dataMode]);

  // Сохраняем ссылки на функции для использования без циклических зависимостей
  connectRef.current = connect;

  // Подписка на символы
  const subscribe = useCallback((symbols) => {
    if (!Array.isArray(symbols) || symbols.length === 0) return;

    const newSymbols = symbols.filter(s => !subscribedSymbolsRef.current.has(s));
    if (newSymbols.length === 0) return;

    const wasEmpty = subscribedSymbolsRef.current.size === 0;
    newSymbols.forEach(s => subscribedSymbolsRef.current.add(s));

    if (wasEmpty && (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN)) {
      connectRef.current?.();
    } else if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action: 'subscribe', symbols: newSymbols }));
    }
  }, []);

  subscribeRef.current = subscribe;

  // Отписка от символов
  const unsubscribe = useCallback((symbols) => {
    if (!Array.isArray(symbols) || symbols.length === 0) return;
    symbols.forEach(s => subscribedSymbolsRef.current.delete(s));
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action: 'unsubscribe', symbols: symbols }));
    }
  }, []);

  // Загрузка начальных цен для контрактов
  // ОПТИМИЗАЦИЯ: batchSize=2 чтобы не забивать очередь HTTP соединений браузера
  // ЗАЧЕМ: Браузер ограничивает 6 параллельных соединений к домену
  const loadInitialPrices = useCallback(async (symbols) => {
    const batchSize = 2;
    for (let i = 0; i < symbols.length; i += batchSize) {
      const batch = symbols.slice(i, i + batchSize);
      await Promise.all(batch.map(async (symbol) => {
        try {
          const url = `${API_BASE_URL}/api/databento/ticker/${symbol}?instrument_type=future`;
          const response = await fetch(url, { headers: { 'X-API-Key': API_KEY } });
          
          if (response.ok) {
            const data = await response.json();
            if (data.price !== null && data.price !== undefined) {
              const dataMode = data.data_mode || data.data_source || 'unknown';
              setQuotes(prev => ({
                ...prev,
                [symbol]: {
                  symbol,
                  price: data.price,
                  bid: data.bid || null,
                  ask: data.ask || null,
                  timestamp: data.timestamp || new Date().toISOString(),
                  isLive: false,
                  dataMode: dataMode
                }
              }));
            }
          }
        } catch (err) {
          // Тихо игнорируем ошибки загрузки
        }
      }));
    }
  }, []);

  // Загрузка всех контрактов и автоматическая подписка
  // ОПТИМИЗАЦИЯ: Разблокируем UI сразу, цены загружаем в фоне с задержкой
  // ЗАЧЕМ: Не блокировать очередь HTTP для критичных запросов (expirations, strikes)
  const loadAllContracts = useCallback((contracts) => {
    if (!contracts) return;
    if (loadedContractsRef.current) return;

    // ✅ Сразу разблокируем селектор контрактов
    setAllContracts(contracts);
    loadedContractsRef.current = true;

    const allSymbols = [];
    Object.values(contracts).forEach(contractsList => {
      contractsList.forEach(contract => allSymbols.push(contract.symbol));
    });

    // ⏳ ОТЛОЖЕННАЯ WebSocket подписка
    // ЗАЧЕМ: Даем время критичным HTTP запросам (expirations, strikes) выполниться
    // до того как WebSocket соединение займет ресурсы браузера
    // Порядок: market-status → all-contracts → expirations → strikes → WebSocket
    setTimeout(() => {
      console.log('🔌 [Futures WS] Отложенная подписка на', allSymbols.length, 'контрактов');
      if (allSymbols.length > 0) subscribeRef.current?.(allSymbols);
    }, 2000);
  }, [loadInitialPrices]);

  // Первоначальное подключение и cleanup
  useEffect(() => {
    if (isWebSocketEnabled && dataMode === 'live') {
      connectRef.current?.();
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        if (wsRef.current.pingInterval) {
          clearInterval(wsRef.current.pingInterval);
        }
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [dataMode]);

  const value = {
    quotes,
    allContracts,
    isConnected,
    isConnecting,
    isPaused,
    dataMode,
    setDataMode,
    subscribe,
    unsubscribe,
    loadAllContracts
  };

  return (
    <FuturesWebSocketContext.Provider value={value}>
      {children}
    </FuturesWebSocketContext.Provider>
  );
};
